<template>
  <div>
  <v-dialog
    v-model="internalValue"
    max-width="900"
    transition="dialog-bottom-transition"
    scrollable
  >
    <v-card class="ds-dialog">
      <!-- Cabeçalho -->
      <v-card-title class="ds-dialog-header">
        <v-icon color="white" class="mr-2" style="position: relative; z-index: 1;">mdi-calendar-plus</v-icon>
        <span class="ds-dialog-title">Adicionar Atendimento</span>
        <v-spacer />
        <v-btn
          icon
          color="white"
          class="close-btn"
          aria-label="Ver tour deste formulário"
          @click="startTour"
        >
          <v-icon>mdi-help-circle-outline</v-icon>
        </v-btn>
        <v-btn icon color="white" class="close-btn" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- ✅ Barra de carregamento (enquanto carrega clínicas/médicos/pacientes/etc) -->
      <v-progress-linear
        v-if="loadingAddNewEventData"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <v-card-text class="ds-dialog-body add-event-body">
        <v-form ref="appointmentForm" v-model="formValid">
          <div class="add-event-grid">
            <div class="ae-col">
          <!-- ═══ Participantes ═══ -->
          <section class="ds-form-section" data-tour="ev-participantes">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-account-group</v-icon>
                <span>Participantes</span>
              </div>
            </div>
            <div>
              <v-row dense>
                <!-- Se médico de clínica ou assistente -->
                <v-col cols="12" v-if="userType !== 'doctor_solo'">
                  <v-select
                    label="Clínica"
                    v-model="selectedClinicId"
                    :items="clinics"
                    item-text="name"
                    item-value="id"
                    @change="onClinicSelected()"
                    prepend-inner-icon="mdi-hospital-building"
                    outlined
                    dense
                    hide-details="auto"
                    color="primary"
                    :rules="[(v) => !!v || 'Selecione uma clínica']"
                    :item-disabled="(item) => !item.has_google_account"
                    :loading="loadingAddNewEventData"
                    :disabled="loadingAddNewEventData"
                  >
                    <template v-slot:item="{ item }">
                      <div class="py-2">
                        <div class="font-weight-medium">{{ item.name }}</div>
                        <!-- Só clínica com conta Google vinculada pode ser selecionada -->
                        <div
                          v-if="item.has_google_account"
                          class="text--secondary text-caption"
                        >
                          CNPJ: {{ item.cnpj }} - Estado: {{ item.state }} - Cidade:
                          {{ item.city }}
                        </div>
                        <div v-else class="text-caption red--text font-weight-bold">
                          ⚠ Conta Google não vinculada
                        </div>
                      </div>
                    </template>
                  </v-select>
                </v-col>

                <!-- Se assistente, mostra também médicos -->
                <v-col cols="12" v-if="userType === 'assistant'">
                  <v-autocomplete
                    label="Médico"
                    v-model="selectedDoctorId"
                    :items="doctors"
                    item-text="name"
                    item-value="id"
                    @change="fetchDoctorSoloPatients(selectedDoctorId)"
                    outlined
                    dense
                    hide-details="auto"
                    color="primary"
                    :rules="[(v) => !!v || 'Selecione um médico']"
                    :item-disabled="(item) => !item.email_gmail"
                    :loading="loadingAddNewEventData"
                    :disabled="loadingAddNewEventData || !selectedClinicId"
                  >
                    <template v-slot:item="{ item }">
                      <div class="py-2">
                        <div class="font-weight-medium">{{ item.name }}</div>
                        <div
                          v-if="item.email_gmail"
                          class="text--secondary text-caption"
                        >
                          CRM: {{ item.crm }} - E-mail: {{ item.email }} - Gmail:
                          {{ item.email_gmail }}
                        </div>
                        <div v-else class="text-caption red--text font-weight-bold">
                          ⚠ Conta Google não vinculada
                        </div>
                      </div>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Paciente (todos os casos) -->
                <v-col cols="12">
                  <v-autocomplete
                    label="Paciente"
                    v-model="newEvent.patient"
                    :items="
                      userType === 'doctor_solo' ? patients : patientsByClinic
                    "
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    hide-details="auto"
                    color="primary"
                    prepend-inner-icon="mdi-account"
                    :rules="[(v) => !!v || 'Selecione um paciente']"
                    :loading="loadingAddNewEventData"
                    :disabled="loadingAddNewEventData"
                  >
                    <template v-slot:item="{ item }">
                      <div class="py-2">
                        <div class="font-weight-medium">{{ item.name }}</div>
                        <div class="text--secondary text-caption">
                          Telefone: {{ item.phone }} - E-mail: {{ item.email }}
                        </div>
                      </div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <div class="font-weight-medium">{{ item.name }}</div>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Agendamentos futuros do paciente (só aparece se houver) -->
                <v-col cols="12" v-if="patientFutureEvents.length" class="pt-0">
                  <v-expansion-panels flat class="future-events-panel">
                    <v-expansion-panel>
                      <v-expansion-panel-header class="px-3 py-1">
                        <div class="d-flex align-center">
                          <v-badge
                            :content="patientFutureEvents.length"
                            color="red"
                            overlap
                          >
                            <v-icon color="amber darken-3">
                              mdi-bell-ring-outline
                            </v-icon>
                          </v-badge>
                          <span class="ml-4 text-body-2 font-weight-medium">
                            Este paciente já possui
                            {{ patientFutureEvents.length }} agendamento(s)
                            futuro(s)
                          </span>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-list dense class="py-0">
                          <v-list-item
                            v-for="ev in patientFutureEvents"
                            :key="ev.id"
                            class="px-0"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon small color="amber darken-2">
                                mdi-calendar-clock
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-body-2">
                                {{ formatEventDateTime(ev.start) }}
                              </v-list-item-title>
                              <v-list-item-subtitle
                                v-if="ev.title"
                                class="text-caption"
                              >
                                {{ ev.title }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
            </div>
          </section>

          <!-- ═══ Data e horário ═══ -->
          <section class="ds-form-section" data-tour="ev-datahora">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                <span>Data e horário</span>
              </div>
            </div>
            <div>
              <v-row dense>
                <!-- Data do Atendimento (dias fora da disponibilidade ficam desabilitados) -->
                <v-col cols="12" sm="6">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                    :disabled="loadingAddNewEventData"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formattedDayDate"
                        label="Data do Atendimento"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details="auto"
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        :rules="[() => !!newEvent.dayDate || 'Obrigatório']"
                        :disabled="loadingAddNewEventData"
                      />
                    </template>
                    <v-date-picker
                      v-model="newEvent.dayDate"
                      :min="today"
                      :events="isWorkingDay"
                      event-color="#3E8E73"
                      :picker-date.sync="pickerMonth"
                      locale="pt-BR"
                      color="primary"
                      no-title
                      scrollable
                      class="agenda-highlight-picker"
                      @input="dateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <div
                    v-if="availabilityHint"
                    class="caption grey--text text--darken-1 mt-1 d-flex align-center"
                  >
                    <v-icon x-small class="mr-1">mdi-calendar-check</v-icon>
                    {{ availabilityHint }}
                  </div>
                </v-col>

                <!-- Horários -->
                <v-col cols="6" sm="3">
                  <v-autocomplete
                    v-model="newEvent.beginTime"
                    :items="allowedBeginTimes"
                    label="Início"
                    prepend-inner-icon="mdi-clock-outline"
                    outlined
                    dense
                    hide-details="auto"
                    color="primary"
                    :rules="[(v) => !!v || 'Obrigatório']"
                    @change="onBeginTimeChange"
                    :disabled="loadingAddNewEventData"
                    :item-disabled="isBusyTime"
                  >
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item
                        v-bind="attrs"
                        v-on="on"
                        :class="[
                          'agenda-time-item',
                          {
                            'agenda-time-item--on':
                              isWorkingTime(item) && !isBusyTime(item),
                            'agenda-time-item--commitment':
                              isCommitmentTime(item),
                            'agenda-time-item--taken':
                              isBusyTime(item) && !isCommitmentTime(item),
                          },
                        ]"
                      >
                        <v-list-item-title>{{ item }}</v-list-item-title>
                        <v-icon
                          v-if="isCommitmentTime(item)"
                          small
                          class="agenda-time-item__mark ml-2"
                        >mdi-calendar-account</v-icon>
                        <v-icon
                          v-else-if="isBusyTime(item)"
                          small
                          class="agenda-time-item__mark ml-2"
                        >mdi-account-clock</v-icon>
                        <v-icon
                          v-else-if="isWorkingTime(item)"
                          small
                          class="agenda-time-item__check ml-2"
                        >mdi-check</v-icon>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-autocomplete
                    v-model="newEvent.endTime"
                    :items="allowedEndTimes"
                    label="Fim"
                    prepend-inner-icon="mdi-clock-end"
                    outlined
                    dense
                    hide-details="auto"
                    color="primary"
                    :rules="[
                      (v) => !!v || 'Obrigatório',
                      (v) =>
                        v !== newEvent.beginTime ||
                        'Horário de fim deve ser diferente do início',
                    ]"
                    :disabled="loadingAddNewEventData"
                    :item-disabled="isBusyEndTime"
                  >
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item
                        v-bind="attrs"
                        v-on="on"
                        :class="[
                          'agenda-time-item',
                          {
                            'agenda-time-item--on':
                              isWorkingEndTime(item) && !isBusyEndTime(item),
                            'agenda-time-item--commitment':
                              isCommitmentEndTime(item),
                            'agenda-time-item--taken':
                              isBusyEndTime(item) && !isCommitmentEndTime(item),
                          },
                        ]"
                      >
                        <v-list-item-title>{{ item }}</v-list-item-title>
                        <v-icon
                          v-if="isCommitmentEndTime(item)"
                          small
                          class="agenda-time-item__mark ml-2"
                        >mdi-calendar-account</v-icon>
                        <v-icon
                          v-else-if="isBusyEndTime(item)"
                          small
                          class="agenda-time-item__mark ml-2"
                        >mdi-account-clock</v-icon>
                        <v-icon
                          v-else-if="isWorkingEndTime(item)"
                          small
                          class="agenda-time-item__check ml-2"
                        >mdi-check</v-icon>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
            </div>
          </section>

          <!-- ═══ Observações ═══ -->
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-note-text-outline</v-icon>
                <span>Observações</span>
                <span class="ds-form-section__optional">opcional</span>
              </div>
            </div>
            <div>
              <v-textarea
                v-model="newEvent.description"
                rows="2"
                auto-grow
                prepend-inner-icon="mdi-pencil"
                outlined
                dense
                hide-details="auto"
                color="primary"
                :disabled="loadingAddNewEventData"
              />
            </div>
          </section>
            </div>
            <div class="ae-col">

          <!-- ═══ Detalhes da consulta ═══ -->
          <section class="ds-form-section" data-tour="ev-detalhes">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-clipboard-text</v-icon>
                <span>Detalhes da consulta</span>
              </div>
            </div>
            <div>
              <!-- Tipo de Consulta -->
              <v-radio-group
                v-model="selectedAppointmentType"
                color="primary"
                class="mt-0 mb-1"
                hide-details="auto"
                :rules="[(v) => !!v || 'Selecione o tipo de consulta']"
                :disabled="loadingAddNewEventData"
              >
                <template v-slot:label>
                  <span class="field-label">Tipo de consulta</span>
                </template>
                <v-radio
                  v-for="type in appointmentTypes"
                  :key="type.value"
                  :label="type.label"
                  :value="type.value"
                />
              </v-radio-group>

              <!-- Consulta personalizada -->
              <v-text-field
                v-if="selectedAppointmentType === 'Outra'"
                label="Especifique o tipo de consulta"
                v-model="otherAppointmentType"
                prepend-inner-icon="mdi-pencil"
                outlined
                dense
                hide-details="auto"
                color="primary"
                class="mt-2"
                :disabled="loadingAddNewEventData"
              />

              <!-- Anamneses a enviar: padroes pre-selecionadas + personalizadas,
                   tudo numa lista so. O paciente recebe todas em sequencia. -->
              <v-select
                v-if="selectedAppointmentType === 'Primeira consulta'"
                v-model="selectedAnamneseItems"
                :items="anamneseSelectItems"
                item-text="label"
                item-value="value"
                label="Anamneses a Enviar"
                prepend-inner-icon="mdi-clipboard-text"
                outlined
                dense
                hide-details="auto"
                color="primary"
                class="mt-3"
                multiple
                chips
                small-chips
                deletable-chips
                :disabled="loadingAddNewEventData"
                hint="As padroes ja vem selecionadas; adicione ou remova conforme a consulta."
                persistent-hint
              >
                <!-- Chip próprio: o padrão do Vuetify não corta nome longo e
                     empurrava o X para fora (ver .ds-chip-label em main.css). -->
                <template v-slot:selection="{ item, parent }">
                  <v-chip
                    small
                    close
                    :disabled="loadingAddNewEventData"
                    @click:close="parent.selectItem(item)"
                  >
                    <span class="ds-chip-label" :title="item.label">{{ item.label }}</span>
                  </v-chip>
                </template>
              </v-select>

              <v-divider class="my-3"></v-divider>

              <!-- Modalidade do Atendimento (presencial / online) — destaque -->
              <div class="field-label mb-2">Modalidade</div>
              <div class="modality-options">
                <button
                  type="button"
                  class="modality-card"
                  :class="{ 'modality-card--active': newEvent.meetingType === 'in_person' }"
                  :disabled="loadingAddNewEventData"
                  @click="newEvent.meetingType = 'in_person'"
                >
                  <v-icon
                    class="modality-icon"
                    :color="newEvent.meetingType === 'in_person' ? 'primary' : 'grey lighten-1'"
                  >
                    mdi-hospital-building
                  </v-icon>
                  <div class="modality-text">
                    <span class="modality-title">Presencial</span>
                    <span class="modality-sub">No consultório</span>
                  </div>
                  <v-icon
                    v-if="newEvent.meetingType === 'in_person'"
                    class="modality-check"
                    color="primary"
                  >
                    mdi-check-circle
                  </v-icon>
                </button>

                <button
                  type="button"
                  class="modality-card modality-card--green"
                  :class="{ 'modality-card--active-green': newEvent.meetingType === 'online' }"
                  :disabled="loadingAddNewEventData"
                  @click="newEvent.meetingType = 'online'"
                >
                  <v-icon
                    class="modality-icon"
                    :color="newEvent.meetingType === 'online' ? 'success' : 'grey lighten-1'"
                  >
                    mdi-video
                  </v-icon>
                  <div class="modality-text">
                    <span class="modality-title">Online</span>
                    <span class="modality-sub">Google Meet</span>
                  </div>
                  <v-icon
                    v-if="newEvent.meetingType === 'online'"
                    class="modality-check"
                    color="success"
                  >
                    mdi-check-circle
                  </v-icon>
                </button>
              </div>
              <div v-if="newEvent.meetingType === 'online'" class="meet-hint mt-2">
                <v-icon x-small color="success" class="mr-1">mdi-information</v-icon>
                Um link do Google Meet será criado automaticamente e enviado aos participantes.
              </div>

              <!-- Status do Atendimento -->
              <v-select
                v-model="newEvent.status"
                :items="statusOptions"
                label="Status"
                prepend-inner-icon="mdi-list-status"
                outlined
                dense
                hide-details="auto"
                color="primary"
                class="mt-3"
                :rules="[(v) => !!v || 'Obrigatório']"
                :disabled="loadingAddNewEventData"
              >
                <template v-slot:selection="{ item }">
                  <v-chip :color="item.color" text-color="white" small>
                    {{ item.text }}
                  </v-chip>
                </template>
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-chip :color="item.color" text-color="white" small>
                          {{ item.text }}
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </div>
          </section>
            </div>
          </div>

          <!-- ═══ Encaixe ═══ -->
          <section class="ds-form-section" data-tour="ev-encaixe">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                <span>Encaixe</span>
              </div>
            </div>
            <div>
              <v-switch
                v-model="wantsFitIn"
                inset
                dense
                hide-details
                color="primary"
                class="ma-0"
                label="Deseja encaixe? (ser chamado mais cedo se abrir vaga)"
                :disabled="loadingAddNewEventData"
              />

              <div v-if="wantsFitIn" class="mt-3">
                <div
                  v-for="(opt, i) in fitInOptions"
                  :key="i"
                  class="fitin-option"
                  :class="{ 'mt-3': i > 0 }"
                >
                  <div class="d-flex align-center mb-1">
                    <span class="caption font-weight-bold grey--text text--darken-2">
                      Possibilidade {{ i + 1 }}
                    </span>
                    <v-spacer />
                    <v-btn
                      v-if="fitInOptions.length > 1"
                      icon
                      x-small
                      @click="removeFitInOption(i)"
                    >
                      <v-icon size="16" color="error">mdi-close</v-icon>
                    </v-btn>
                  </div>

                  <p class="caption font-weight-medium grey--text text--darken-1 mb-1">
                    Em quais dias o encaixe pode ser feito?
                  </p>
                  <v-chip-group v-model="opt.weekdays" multiple column>
                    <v-chip
                      v-for="d in weekdaysOptions"
                      :key="d.value"
                      :value="d.value"
                      filter
                      outlined
                      color="primary"
                      small
                    >
                      {{ d.label }}
                    </v-chip>
                  </v-chip-group>

                  <p class="caption font-weight-medium grey--text text--darken-1 mb-1 mt-2">
                    Em qual horário?
                  </p>
                  <v-row dense>
                    <v-col cols="6">
                      <v-text-field
                        v-model="opt.time_from"
                        type="time"
                        label="De"
                        outlined
                        dense
                        hide-details
                        color="primary"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        v-model="opt.time_to"
                        type="time"
                        label="Até"
                        outlined
                        dense
                        hide-details
                        color="primary"
                      />
                    </v-col>
                  </v-row>
                </div>

                <!-- Botão de adicionar outra possibilidade de encaixe -->
                <div class="d-flex align-center mt-2">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        :disabled="!canAddFitInOption"
                        @click="addFitInOption"
                      >
                        <v-icon>mdi-plus-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>
                      {{
                        canAddFitInOption
                          ? "Adicionar outra possibilidade"
                          : "Selecione os dias da possibilidade atual primeiro"
                      }}
                    </span>
                  </v-tooltip>
                  <span class="caption grey--text text--darken-1 ml-1">
                    Adicionar outra possibilidade
                  </span>
                </div>
              </div>
            </div>
          </section>
        </v-form>
      </v-card-text>

      <!-- Botões -->
      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" class="ds-cancel-btn" @click="closeDialog">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="ds-save-btn"
          data-tour="ev-salvar"
          :loading="loading"
          :disabled="loadingAddNewEventData"
          dark
          depressed
          @click="saveAppointment"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

    <!-- Dialog de confirmação para envio de anamnese via WhatsApp -->
    <v-dialog
      v-model="showAnamneseConfirm"
      max-width="520"
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header ds-dialog-header--wa">
          <v-icon color="white" class="mr-2" style="position: relative; z-index: 1;">mdi-whatsapp</v-icon>
          <span class="ds-dialog-title">Enviar Anamnese via WhatsApp</span>
          <v-spacer />
          <v-btn icon color="white" class="close-btn" @click="skipAnamnese">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <p class="mb-3 text-body-1">
              Atendimento criado com sucesso. Deseja iniciar a anamnese pelo WhatsApp agora?
            </p>

            <div
              v-if="pendingAnamnese"
              class="anamnese-info-box"
            >
              <div class="d-flex align-center mb-2">
                <v-icon small color="primary" class="mr-2">mdi-account</v-icon>
                <span class="font-weight-medium">{{ pendingAnamnese.patient.name }}</span>
              </div>
              <div
                v-if="pendingAnamnese.patient.phone"
                class="d-flex align-center mb-2"
              >
                <v-icon small color="success" class="mr-2">mdi-phone</v-icon>
                <span>{{ pendingAnamnese.patient.phone }}</span>
              </div>
              <div
                v-else
                class="d-flex align-center mb-2 red--text"
              >
                <v-icon small color="red" class="mr-2">mdi-phone-off</v-icon>
                <span class="font-weight-medium">Paciente sem telefone cadastrado</span>
              </div>
              <div
                v-if="pendingAnamnese.anamneseLabels && pendingAnamnese.anamneseLabels.length"
                class="d-flex align-center"
              >
                <v-icon small color="primary" class="mr-2">mdi-clipboard-text</v-icon>
                <span class="text-caption">
                  Anamneses: {{ pendingAnamnese.anamneseLabels.join(", ") }}
                </span>
              </div>
            </div>
          </section>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn
            outlined
            color="primary"
            class="ds-cancel-btn"
            :disabled="sendingAnamnese"
            @click="skipAnamnese"
          >
            <v-icon left>mdi-close</v-icon>
            Agora não
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            class="ds-save-btn"
            :loading="sendingAnamnese"
            :disabled="!pendingAnamnese || !pendingAnamnese.patient.phone"
            dark
            depressed
            @click="confirmSendAnamnese"
          >
            <v-icon left>mdi-whatsapp</v-icon>
            Enviar agora
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import http from "@/services/http";
import {
  createFitInRequest,
  WEEKDAYS as FIT_IN_WEEKDAYS,
} from "@/services/fitInRequestService";
import { useSnackbar } from "@/composables/useSnackbar";
import {
  getMyClinics,
  getDoctorsByClinics,
  getAssistantsByClinics,
} from "@/services/clinicService";
import { listAnamneseTemplates } from "@/services/anamneseTemplateService";
import scheduleAvailabilityMixin from "@/features/calendar/mixins/scheduleAvailabilityMixin";
import tourDialogMixin from "@/features/tour/tourDialogMixin";
import { todayISO, userTimeZone } from "@/utils/datetime";

export default {
  mixins: [scheduleAvailabilityMixin, tourDialogMixin],

  props: {
    value: { type: Boolean, default: false },
    preSelectedPatient: {
      type: Object,
      default: null,
    },
    preSelectedDate: {
      type: String,
      default: null,
    },
    preSelectedTime: {
      type: String,
      default: null,
    },
  },
  emits: ["update:dialogAddEvent", "eventSaved"],
  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },
  data() {
    const today = todayISO();
    return {
      clinics: [],
      doctors: [],
      assistants: [],
      selectedClinicId: null,
      selectedDoctorId: null,
      menuData: false,
      doctorName: "",
      patients: [],
      patientsByClinic: [],
      selectedAppointmentType: "",
      otherAppointmentType: "",
      appointmentTypes: [
        { label: "Primeira consulta", value: "Primeira consulta" },
        {
          label: "Consulta de acompanhamento",
          value: "Consulta de acompanhamento",
        },
        { label: "Retorno", value: "Retorno" },
        { label: "Outra, especificar", value: "Outra" },
      ],
      // Fila unificada de anamneses da consulta: padroes (geral/nutricionista/
      // exames) + personalizadas (tokens "tmpl:<id>"), numa lista so. As padroes
      // ja vem pre-selecionadas; o medico adiciona/remove por consulta.
      selectedAnamneseItems: ["geral"],
      anamneseTypes: [
        { label: "Geral", value: "geral" },
        { label: "Nutricionista", value: "nutricionista" },
        { label: "Exames", value: "exames" },
      ],
      // Anamneses personalizadas ATIVAS do medico. Entram na mesma lista como
      // itens "tmpl:<id>"; podem ser varias na mesma consulta.
      customTemplates: [],
      // "Compromisso" (blocked) removido a pedido do usuario (24/09): sem
      // coluna no kanban, quem selecionasse esse status via aqui criava um
      // evento que nao aparecia em lugar nenhum -- nem erro, nem card, so
      // sumia. Nao ha outro fluxo dedicado a bloqueio de horario que dependa
      // desta opcao.
      statusOptions: [
        { text: "Em espera", value: "waiting", color: "#FFC107" },
        { text: "Agendado", value: "scheduled", color: "#2196F3" },
        { text: "Confirmado", value: "confirmed", color: "#4CAF50" },
        { text: "Cancelado", value: "canceled", color: "#F44336" },
      ],
      loading: false,
      formValid: false,
      loadingAddNewEventData: false,
      loadingCount: 0,
      showAnamneseConfirm: false,
      sendingAnamnese: false,
      pendingAnamnese: null,
      today,
      newEvent: {
        patient: "",
        dayDate: today,
        beginTime: "",
        endTime: "",
        doctor: "",
        description: "",
        status: "scheduled",
        meetingType: "in_person",
      },
      // Agendamentos futuros do paciente selecionado (painel expansível).
      patientFutureEvents: [],
      loadingFutureEvents: false,
      // Encaixe (fit-in)
      wantsFitIn: false,
      // Várias possibilidades de encaixe: [{ weekdays, time_from, time_to }]
      fitInOptions: [{ weekdays: [], time_from: null, time_to: null }],
    };
  },
  computed: {
    userType() {
      return this.$store.getters["auth/userType"];
    },
    userData() {
      return this.$store.getters["auth/user"];
    },

    // ── Tour guiado (dispara na 1ª abertura; "?" no cabeçalho) ─────────
    tourId() {
      return "dialog-novo-evento";
    },

    // ── Contrato do scheduleAvailabilityMixin ──────────────────────────
    // Clínica/médico em foco (solo não tem clínica → sem regra).
    scheduleClinicId() {
      if (this.userType === "doctor_solo") return null;
      return this.selectedClinicId || null;
    },
    scheduleDoctorId() {
      if (this.userType === "assistant") return this.selectedDoctorId || null;
      return this.userData?.id || null;
    },
    scheduleDate() {
      return this.newEvent.dayDate || null;
    },
    scheduleBeginTime() {
      return this.newEvent.beginTime || "";
    },
    // scheduleAllTimes agora vem do mixin (fonte única em buildTimeGrid()).

    // Evita empilhar possibilidades vazias.
    canAddFitInOption() {
      const last = this.fitInOptions[this.fitInOptions.length - 1];
      return !!last && last.weekdays.length > 0;
    },

    formattedDayDate() {
      return this.formatDayDate(this.newEvent.dayDate);
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    weekdaysOptions() {
      return FIT_IN_WEEKDAYS;
    },
    // Itens da lista unica de anamneses: padroes + personalizadas ("tmpl:<id>").
    anamneseSelectItems() {
      const customs = (this.customTemplates || []).map((t) => ({
        label: t.name,
        value: `tmpl:${t.id}`,
      }));
      return [...this.anamneseTypes, ...customs];
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetForm();
        this.init(); // já resolve tudo
        this.tourOnOpen(); // tour guiado na 1ª vez que abre
      }
    },
    // Ao escolher/trocar o paciente, busca os agendamentos futuros dele.
    "newEvent.patient"(patientId) {
      this.fetchPatientFutureEvents(patientId);
    },
    // Trocar clínica/médico recalcula os dias/horários bloqueados (mixin).
    selectedClinicId() {
      this.resetSchedule();
    },
    selectedDoctorId() {
      this.resetSchedule();
    },
    // Trocar o dia: garante o mês carregado e limpa horário fora da nova janela.
    "newEvent.dayDate"(v) {
      if (v && this.hasClinicDoctor) {
        this.loadAllowedDatesForMonth(v.slice(0, 7));
      }
      if (
        this.dayWindows &&
        this.newEvent.beginTime &&
        !this.allowedBeginTimes.includes(this.newEvent.beginTime)
      ) {
        this.newEvent.beginTime = "";
        this.newEvent.endTime = "";
      }
    },
  },
  methods: {
    // --------------------------
    // TOUR GUIADO (tourDialogMixin fornece tourOnOpen/startTour)
    // --------------------------
    tourReady() {
      return !this.loadingAddNewEventData;
    },
    tourSteps() {
      // O bloco "Participantes" muda conforme o papel do usuário:
      //  - doctor_solo  → só paciente (sem clínica/médico)
      //  - doctor_clinic → clínica + paciente
      //  - assistant    → clínica + médico + paciente
      const t = this.userType;
      const steps = [];

      if (t === "doctor_solo") {
        steps.push({
          anchor: "ev-participantes",
          title: "Escolha o paciente",
          description:
            "Você atende de forma autônoma (sem clínica), então basta escolher o paciente do atendimento. O evento é criado na SUA conta Google — se ela não estiver vinculada, a agenda avisa e oferece o botão para vincular.",
        });
      } else if (t === "assistant") {
        steps.push(
          {
            anchor: "ev-participantes",
            title: "Clínica, médico e paciente",
            description:
              "Como assistente, escolha nesta ordem: primeiro a clínica; depois o médico daquela clínica (o campo de médico aparece só após escolher a clínica); e por fim o paciente. É com esses três dados que o convite do evento é montado.",
          },
          {
            anchor: "ev-participantes",
            title: "Clínica precisa ter Conta Google",
            description:
              "Só aparecem selecionáveis as clínicas com Conta Google vinculada — as demais ficam desabilitadas na lista. Se a clínica que você quer está bloqueada, peça para vinculá-la em “Minhas Clínicas” (botão “Vincular Conta Google” no card).",
          },
        );
      } else {
        // doctor_clinic
        steps.push(
          {
            anchor: "ev-participantes",
            title: "Clínica e paciente",
            description:
              "Escolha a clínica onde o atendimento acontece e, em seguida, o paciente. O convite do evento é criado na conta Google dessa clínica.",
          },
          {
            anchor: "ev-participantes",
            title: "Clínica precisa ter Conta Google",
            description:
              "Só aparecem selecionáveis as clínicas com Conta Google vinculada — as demais ficam desabilitadas na lista. Se a clínica que você quer está bloqueada, vá em “Minhas Clínicas” e clique em “Vincular Conta Google” no card dela; depois volte aqui para agendar.",
          },
        );
      }

      steps.push(
        {
          anchor: "ev-datahora",
          title: "Data e horário",
          description:
            t === "doctor_solo"
              ? "Selecione o dia e os horários de início e fim. Os dias/horários em que você atende ficam destacados em verde — mas nada impede encaixar fora deles em emergências."
              : "Selecione o dia e os horários de início e fim. Os dias/horários em que o médico atende ficam destacados em verde — mas você pode encaixar fora deles em emergências.",
        },
        {
          anchor: "ev-detalhes",
          title: "Detalhes da consulta",
          description:
            "Defina o tipo de atendimento e a modalidade (presencial ou online). No online, o link da reunião é gerado automaticamente e vai junto no convite.",
        },
        {
          anchor: "ev-encaixe",
          title: "Encaixe",
          description:
            "Marque “Deseja encaixe?” para sinalizar que o paciente aceita ser chamado mais cedo caso abra uma vaga — útil para preencher horários que vagam de última hora.",
        },
        {
          anchor: "ev-salvar",
          title: "Salvar o atendimento",
          description:
            "Quando estiver tudo certo, salve. O evento vai para a agenda e o convite é enviado por e-mail aos participantes, com os detalhes da clínica e do médico.",
        },
      );

      return steps;
    },

    // --------------------------
    // ✅ LOADING HELPERS (robusto p/ requests concorrentes)
    // --------------------------
    beginCalendarLoading() {
      this.loadingCount += 1;
      this.loadingAddNewEventData = true;
    },
    endCalendarLoading() {
      this.loadingCount = Math.max(0, this.loadingCount - 1);
      this.loadingAddNewEventData = this.loadingCount > 0;
    },

    // --------------------------
    // BOOT / INIT
    // --------------------------
    async init() {
      this.beginCalendarLoading();
      try {
        // garante user/userType (no máximo 1 request)
        await this.$store.dispatch("auth/refreshMe");

        // Anamneses personalizadas ativas (opcional no envio). Defensivo: falha
        // aqui NAO pode quebrar o dialog de agendamento.
        this.loadCustomAnamneseTemplates();

        // clinics necessárias quando não é doctor_solo
        if (this.userType !== "doctor_solo") {
          this.clinics = (await getMyClinics()) || [];
        }

        if (this.userType === "doctor_solo") {
          this.doctorName = this.userData?.name || "";
          this.newEvent.doctor = this.userData?.email || "";
          await this.fetchDoctorSoloPatients(this.userData?.id);
          return;
        }

        if (this.clinics.length > 0) {
          if (!this.selectedClinicId)
            this.selectedClinicId = this.clinics[0].id;
          await this.onClinicSelected();
          this.applyPreSelectedPatient();
        }
      } catch (err) {
        console.error("Erro ao inicializar dialog de atendimento:", err);
        this.erroDeRequisicao(err, "Erro ao carregar dados iniciais. Tente novamente.");
      } finally {
        this.endCalendarLoading();
      }
    },
    closeDialog() {
      this.$emit("input", false);
      this.resetForm();
    },
    resetForm() {
      let roundedTime = this.preSelectedTime || "";
      if (roundedTime) {
        const [hh, mm] = roundedTime.split(":").map(Number);
        const roundedMm = mm >= 30 ? "30" : "00";
        roundedTime = `${String(hh).padStart(2, "0")}:${roundedMm}`;
      }

      this.newEvent = {
        patient: "",
        dayDate: this.preSelectedDate || this.today,
        beginTime: roundedTime,
        endTime: "",
        doctor: "",
        description: "",
        status: "scheduled",
        meetingType: "in_person",
      };
      if (roundedTime) {
        this.onBeginTimeChange();
      }
      this.selectedClinicId = null;
      this.selectedDoctorId = null;
      this.selectedAppointmentType = "";
      this.otherAppointmentType = "";
      this.selectedAnamneseItems = ["geral"];
      this.patients = [];
      this.patientsByClinic = [];
      this.doctors = [];
      this.assistants = [];
      this.patientFutureEvents = [];
      this.wantsFitIn = false;
      this.fitInOptions = this.blankFitInOptions();
      this.$nextTick(() => {
        this.$refs.appointmentForm?.resetValidation();
      });
    },

    // --------------------------
    // UI HELPERS
    // --------------------------
    blankFitInOptions() {
      return [{ weekdays: [], time_from: null, time_to: null }];
    },

    addFitInOption() {
      if (!this.canAddFitInOption) return;
      this.fitInOptions.push({ weekdays: [], time_from: null, time_to: null });
    },

    removeFitInOption(index) {
      this.fitInOptions.splice(index, 1);
      if (!this.fitInOptions.length) {
        this.fitInOptions = this.blankFitInOptions();
      }
    },

    onBeginTimeChange() {
      this.newEvent.endTime = this.computeEndTime(this.newEvent.beginTime);
    },

    // --------------------------
    // CLINIC SELECTION
    // --------------------------
    async onClinicSelected() {
      if (!this.selectedClinicId) return;

      this.beginCalendarLoading();
      try {
        if (this.userType === "assistant") {
          this.selectedDoctorId = null;
          this.doctors = [];
          await this.fetchDoctorsByClinic(this.selectedClinicId);
        }

        await Promise.all([
          this.fetchPatientsByClinic(this.selectedClinicId),
          this.fetchAssistantsByClinic(this.selectedClinicId),
        ]);
      } finally {
        this.endCalendarLoading();
      }
    },

    // --------------------------
    // BUSINESS RULES (mantive igual)
    // --------------------------
    validateGoogleAccounts() {
      // Evento único: o evento é sempre criado na agenda do MÉDICO (dono).
      // A clínica entra apenas como convidada (opcional) — não exigimos mais
      // que a clínica tenha conta Google vinculada.
      if (this.userType === "assistant") {
        const selectedDoctor = this.doctors.find(
          (d) => d.id === this.selectedDoctorId,
        );
        if (!selectedDoctor?.email_gmail) {
          this.error("O médico selecionado não possui conta Google vinculada.");
          return false;
        }
      } else if (this.userType === "doctor_clinic") {
        if (!this.userData?.email_gmail) {
          this.error("Sua conta não possui Gmail vinculado.");
          return false;
        }
      } else if (this.userType === "doctor_solo") {
        if (!this.userData?.email_gmail) {
          this.error("Sua conta não possui Gmail vinculado.");
          return false;
        }
      }

      return true;
    },

    getAppointmentTypeDescription() {
      if (this.selectedAppointmentType === "Outra")
        return this.otherAppointmentType;
      return this.selectedAppointmentType || "";
    },
    getBrowserTimeZone: userTimeZone,

    buildLocalDateTime(dayDate, timeHHmm) {
      const [y, m, d] = dayDate.split("-").map(Number);
      const [hh, mm] = timeHHmm.split(":").map(Number);
      return new Date(y, m - 1, d, hh, mm, 0, 0);
    },

    getStartUtcIso() {
      return this.buildLocalDateTime(
        this.newEvent.dayDate,
        this.newEvent.beginTime,
      ).toISOString();
    },

    getSelectedDoctorName() {
      const doctor = this.doctors.find((d) => d.id === this.selectedDoctorId);
      return doctor?.name || "";
    },

    getEndUtcIso() {
      return this.buildLocalDateTime(
        this.newEvent.dayDate,
        this.newEvent.endTime,
      ).toISOString();
    },

    // Hook do scheduleAvailabilityMixin: após carregar a disponibilidade, se a
    // A disponibilidade não restringe mais a data — os dias de atendimento só
    // ficam destacados no calendário; não movemos mais a data automaticamente.

    getSelectedPatient() {
      const list =
        this.userType === "doctor_solo" ? this.patients : this.patientsByClinic;
      return list.find((p) => p.id === this.newEvent.patient);
    },

    // Busca os agendamentos futuros (não cancelados) do paciente selecionado.
    async fetchPatientFutureEvents(patientId) {
      if (!patientId) {
        this.patientFutureEvents = [];
        return;
      }
      this.loadingFutureEvents = true;
      try {
        const { data } = await http.get("/api/events/upcoming", {
          params: { patient_id: patientId },
        });
        this.patientFutureEvents = data?.data || [];
      } catch (error) {
        console.error("Erro ao buscar agendamentos futuros do paciente:", error);
        this.patientFutureEvents = [];
      } finally {
        this.loadingFutureEvents = false;
      }
    },

    formatEventDateTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
        d.getHours(),
      )}:${pad(d.getMinutes())}`;
    },

    getAppointmentDescription() {
      const type = this.getAppointmentTypeDescription();
      const notes = this.newEvent.description?.trim();
      let full = `Tipo de consulta: ${type}`;
      if (notes) full += ` - Observações: ${notes}`;
      return full;
    },

    buildEventData() {
      const selectedPatient = this.getSelectedPatient();
      const description = this.getAppointmentDescription();

      let attendees = [selectedPatient.email];
      let doctorId = null;
      let clinicId = null;
      let doctorName = "Médico";

      // Evento único: o médico é o DONO (organizador) do evento, então NÃO
      // entra como convidado. A clínica é injetada como convidada no backend.
      if (this.userType === "assistant") {
        doctorId = this.selectedDoctorId;
        clinicId = this.selectedClinicId;

        const selectedDoctor = this.doctors.find(
          (d) => d.id === this.selectedDoctorId,
        );
        doctorName = selectedDoctor?.name || "Médico";
      } else if (this.userType === "doctor_clinic") {
        doctorId = this.userData?.id;
        clinicId = this.selectedClinicId;
        doctorName = this.userData?.name || "Médico";
      } else if (this.userType === "doctor_solo") {
        doctorId = this.userData?.id;
        doctorName = this.userData?.name || "Médico";
      }

      const assistantsEmails = this.assistants
        .map((a) => a.email_gmail)
        .filter(Boolean);
      attendees = [...attendees, ...assistantsEmails];

      return {
        clinic_id: clinicId,
        doctor_id: doctorId,
        patient_id: selectedPatient.id,
        summary: `${selectedPatient.name} consulta com Dr(a): ${doctorName}`,
        description,
        start_date: this.getStartUtcIso(),
        end_date: this.getEndUtcIso(),
        time_zone: this.getBrowserTimeZone(),
        attendees,
        status: this.newEvent.status,
        meeting_type: this.newEvent.meetingType || "in_person",
      };
    },

    async saveAppointment() {
      try {
        this.loading = true;

        const isValid = await this.$refs.appointmentForm.validate();
        if (!isValid) {
          this.error("Preencha todos os campos obrigatórios.");
          return;
        }

        if (!this.validateGoogleAccounts()) return;

        const selectedPatient = this.getSelectedPatient();
        if (!selectedPatient?.id) {
          this.error("Selecione um paciente válido.");
          return;
        }

        const eventData = this.buildEventData();

        const doctorId = this.userData?.id || this.selectedDoctorId;
        await this.createRelationIfNeeded(doctorId, selectedPatient.id);

        const createdEvent = await this.sendEventToGoogleCalendar(eventData);

        // Pedido de encaixe (se marcado). Não bloqueia o fluxo: o atendimento
        // já foi salvo mesmo que o encaixe falhe.
        if (this.wantsFitIn) {
          // Uma requisição por possibilidade preenchida.
          for (const opt of this.fitInOptions.filter((o) => o.weekdays.length)) {
            try {
              await createFitInRequest({
                patient_id: selectedPatient.id,
                doctor_id: eventData.doctor_id || null,
                clinic_id: eventData.clinic_id || null,
                event_id: createdEvent?.event?.id || null,
                weekdays: opt.weekdays,
                time_from: opt.time_from || null,
                time_to: opt.time_to || null,
              });
            } catch (e) {
              console.error("Erro ao criar pedido de encaixe:", e);
            }
          }
        }

        // Se for primeira consulta, prepara contexto para o dialog de WhatsApp.
        // O dialog é independente: se a chamada externa falhar, o evento já está salvo.
        const isFirstConsultation =
          this.selectedAppointmentType === "Primeira consulta";

        let anamneseContext = null;
        if (isFirstConsultation) {
          anamneseContext = {
            patient: {
              id: selectedPatient.id,
              name: selectedPatient.name,
              phone: selectedPatient.phone,
            },
            doctorName:
              this.userType === "assistant"
                ? this.getSelectedDoctorName()
                : null,
            doctorId:
              this.userType === "assistant"
                ? this.selectedDoctorId
                : this.userData?.id || null,
            // Fila unificada de tokens (defaults + "tmpl:<id>") e rotulos legiveis.
            anamneseItems: [...(this.selectedAnamneseItems || [])],
            anamneseLabels: (this.selectedAnamneseItems || []).map(
              (v) =>
                (this.anamneseSelectItems.find((i) => i.value === v) || {})
                  .label || v,
            ),
            appointmentDate: this.newEvent.dayDate,
            appointmentStartTime: this.newEvent.beginTime,
            appointmentEndTime: this.newEvent.endTime,
          };
        }

        const meetingLink = createdEvent?.event?.meeting_link;
        if (this.newEvent.meetingType === "online" && meetingLink) {
          this.success(
            "Atendimento online criado! Link do Google Meet enviado aos participantes.",
          );
        } else {
          this.success("Atendimento salvo com sucesso!");
        }
        this.$emit("eventSaved");

        if (anamneseContext) {
          // Fecha o dialog principal e abre o de confirmação de anamnese.
          this.pendingAnamnese = anamneseContext;
          this.$emit("input", false);
          this.resetForm();
          this.showAnamneseConfirm = true;
        } else {
          this.closeDialog();
        }

        return createdEvent;
      } catch (err) {
        console.error("Erro ao salvar atendimento:", err);
        // O backend é a autoridade da regra de disponibilidade: quando ele
        // recusa (422), mostra a mensagem dele em vez de um erro genérico.
        const serverMessage = err.response?.data?.message;
        this.erroDeRequisicao(err, 
          err.response?.status === 422 && serverMessage
            ? serverMessage
            : "Erro ao salvar atendimento. Tente novamente.",
        );
      } finally {
        this.loading = false;
      }
    },

    async confirmSendAnamnese() {
      if (!this.pendingAnamnese) return;
      if (!this.pendingAnamnese.patient.phone) {
        this.error("Paciente sem telefone cadastrado.");
        return;
      }

      this.sendingAnamnese = true;
      try {
        await this.sendInitiateAnamnesisData(this.pendingAnamnese);
        this.success("Anamnese enviada com sucesso!");
        this.showAnamneseConfirm = false;
        this.pendingAnamnese = null;
      } catch (err) {
        console.error("Erro ao enviar anamnese:", err);
        const serverMsg = err?.response?.data?.message;
        this.erroDeRequisicao(err, 
          serverMsg
            ? `Não foi possível enviar a anamnese: ${serverMsg}`
            : "Não foi possível enviar a anamnese. O atendimento foi salvo normalmente.",
        );
      } finally {
        this.sendingAnamnese = false;
      }
    },

    skipAnamnese() {
      if (this.sendingAnamnese) return;
      this.showAnamneseConfirm = false;
      this.pendingAnamnese = null;
    },

    async sendEventToGoogleCalendar(eventData) {
      let url;

      if (this.userType === "assistant") {
        url = "/api/google-calendar/create-clinic-event";
      } else if (this.userType === "doctor_clinic") {
        url = "/api/google-calendar/create-doctor-event";
      } else if (this.userType === "doctor_solo") {
        url = "/api/google-calendar/create-solo-event";
      } else {
        throw new Error(
          "Tipo de usuário não suportado para criação de eventos",
        );
      }

      const { data } = await http.post(url, eventData);
      return data;
    },

    async createRelationIfNeeded(doctorId, patientId) {
      try {
        await http.post("/api/relation/create-if-not-exists", {
          user_id: doctorId,
          related_user_id: patientId,
          relation_type: "doctor_patient",
        });
      } catch (err) {
        console.error("Erro ao criar relação médico-paciente:", err);
      }
    },

    // --------------------------
    // FETCHES (com loadingAddNewEventData)
    // --------------------------
    async fetchDoctorsByClinic(clinicId) {
      this.beginCalendarLoading();
      try {
        this.doctors = (await getDoctorsByClinics(clinicId)) || [];
      } finally {
        this.endCalendarLoading();
      }
    },

    async fetchDoctorSoloPatients(doctorId) {
      if (!doctorId) {
        this.patients = [];
        return;
      }

      this.beginCalendarLoading();
      try {
        const { data } = await http.get(
          `/api/relation/patients-by-doctor/${doctorId}`,
        );
        const list = data?.data || [];

        this.patients = list.map((patient) => {
          const primaryPhone = (patient.phones || []).find(
            (p) => p.is_primary == 1,
          );
          return {
            id: patient.id,
            name: patient.name,
            email: patient.email,
            gmail: patient.email_gmail,
            phone: primaryPhone ? primaryPhone.number : null,
          };
        });
      } catch (error) {
        console.error("Erro ao buscar os pacientes:", error);
        this.patients = [];
      } finally {
        this.endCalendarLoading();
      }
    },

    async fetchPatientsByClinic(clinicId) {
      if (!clinicId) {
        this.patientsByClinic = [];
        return;
      }

      this.beginCalendarLoading();
      try {
        const { data } = await http.get(
          `/api/clinic/get-patients-by-clinic/${clinicId}`,
        );
        const list = data?.data || [];

        this.patientsByClinic = list.map((patient) => {
          const primaryPhone = (patient.phones || []).find(
            (p) => p.is_primary == 1,
          );
          return {
            id: patient.id,
            name: patient.name,
            email: patient.email,
            gmail: patient.email_gmail,
            phone: primaryPhone ? primaryPhone.number : null,
          };
        });
      } catch (error) {
        console.error("Erro ao buscar pacientes da clínica:", error);
        this.patientsByClinic = [];
      } finally {
        this.endCalendarLoading();
      }
    },

    async fetchAssistantsByClinic(clinicId) {
      this.beginCalendarLoading();
      try {
        this.assistants = (await getAssistantsByClinics(clinicId)) || [];
      } catch (error) {
        console.error("Erro ao buscar assistentes da clínica:", error);
        this.assistants = [];
      } finally {
        this.endCalendarLoading();
      }
    },

    // --------------------------
    // ENVIO DE ANAMNESE EXTERNA
    // --------------------------
    async loadCustomAnamneseTemplates() {
      try {
        const list = await listAnamneseTemplates();
        // So personalizadas ATIVAS entram no picker (default vai pelos tipos fixos).
        this.customTemplates = (list || []).filter(
          (t) => t && t.active && !t.isDefault,
        );
      } catch (err) {
        console.error("Erro ao carregar anamneses personalizadas:", err);
        this.customTemplates = [];
      }
    },
    async sendInitiateAnamnesisData(context) {
      const {
        patient,
        doctorName,
        doctorId,
        anamneseItems,
        appointmentDate,
        appointmentStartTime,
        appointmentEndTime,
      } = context;

      // Fila unica: defaults (geral/nutricionista/exames) e personalizadas como
      // tokens "tmpl:<id>" viajam juntas em anamnese_type. A comunicacao ordena
      // e consome os itens em sequencia; nao ha mais campo separado de template.
      const payload = {
        patient_id: patient.id,
        phone: patient.phone,
        anamnese_type: (anamneseItems || []).join(","),
      };

      if (doctorName) payload.doctor_name = doctorName;
      if (doctorId) payload.doctor_id = doctorId;
      if (appointmentDate) payload.appointment_date = appointmentDate;
      if (appointmentStartTime)
        payload.appointment_start_time = appointmentStartTime;
      if (appointmentEndTime)
        payload.appointment_end_time = appointmentEndTime;

      if (appointmentDate && appointmentStartTime) {
        payload.appointment_start_datetime = this.buildLocalDateTime(
          appointmentDate,
          appointmentStartTime,
        ).toISOString();
      }
      if (appointmentDate && appointmentEndTime) {
        payload.appointment_end_datetime = this.buildLocalDateTime(
          appointmentDate,
          appointmentEndTime,
        ).toISOString();
      }

      const response = await http.post(
        `/api/external-anamnese/initiate/${patient.id}`,
        payload,
      );
      return response.data;
    },

    applyPreSelectedPatient() {
      if (!this.preSelectedPatient) return;

      this.$nextTick(() => {
        this.newEvent.patient = this.preSelectedPatient.id;
      });
    },

  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.close-btn {
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Action buttons ═══ */
.ds-cancel-btn,
.ds-save-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  height: 40px !important;
}

.ds-save-btn {
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.22) !important;
}

.ds-dialog-header--wa {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 60%, #075e54 100%) !important;
}

.anamnese-info-box {
  background: var(--ds-blue-50);
  border: 1px solid var(--ds-blue-100);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
}

.meet-hint {
  font-size: 12px;
  color: var(--ds-success);
  background: var(--ds-green-surface);
  border: 1px solid var(--ds-green-200);
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
}

/* ═══ Corpo seccionado (alinhado ao CalendarEventDetailsDialog) ═══ */

/* Layout em 2 colunas para caber tudo sem scroll, preservando os cards */
.add-event-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: start;
  margin-bottom: 12px;
}

.ae-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

/* Dentro da coluna o espaçamento vem do gap, não da margem entre seções. */
.ae-col > .ds-form-section + .ds-form-section {
  margin-top: 0;
}

/* Seções depois da grade de 2 colunas. */
.add-event-grid + .ds-form-section {
  margin-top: 0;
}

@media (max-width: 760px) {
  .add-event-grid {
    grid-template-columns: 1fr;
  }
}


/* Compacta os radios (Tipo de consulta) para caber sem scroll */
.add-event-body :deep(.v-radio) {
  margin-bottom: 4px !important;
}

.add-event-body :deep(.v-input--radio-group__input) {
  margin-top: 0 !important;
}

/* Rótulo de grupos (radio-groups) no mesmo tom dos labels dos inputs */
.field-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-550);
}

/* ═══ Seleção de Modalidade — cards com destaque ═══ */
.modality-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.modality-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 2px solid var(--ds-blue-100);
  border-radius: 12px;
  background: var(--ds-surface-white);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.16s ease, box-shadow 0.16s ease,
    background 0.16s ease, transform 0.16s ease;
}

.modality-card:hover:not(:disabled) {
  border-color: var(--ds-blue-200);
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.1);
  transform: translateY(-1px);
}

.modality-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Presencial (azul) ativo */
.modality-card--active {
  border-color: var(--ds-primary);
  background: linear-gradient(135deg, var(--ds-blue-100) 0%, var(--ds-blue-50) 100%);
  box-shadow: 0 6px 16px rgba(31, 77, 63, 0.18);
}

/* Online (verde) ativo */
.modality-card--active-green {
  border-color: var(--ds-success-light);
  background: linear-gradient(135deg, var(--ds-green-surface) 0%, var(--ds-primary-surface) 100%);
  box-shadow: 0 6px 16px rgba(31, 77, 63, 0.18);
}

.modality-icon {
  font-size: 26px !important;
  flex-shrink: 0;
}

.modality-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.modality-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ds-text-strong);
  line-height: 1.2;
}

.modality-sub {
  font-size: 11px;
  color: var(--ds-text-soft);
  line-height: 1.3;
}

.modality-check {
  font-size: 20px !important;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .modality-options {
    grid-template-columns: 1fr;
  }
}

.fitin-option {
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--ds-blue-50);
}

</style>

<!--
  Destaque (não bloqueio) da disponibilidade do médico.
  NÃO pode ser scoped: tanto o menu do autocomplete quanto o date picker são
  anexados fora do componente (em [data-app]), então regras scoped não os alcançam.
-->
<style>
/* Horário em que o médico atende → linha destacada em verde */
.agenda-time-item.agenda-time-item--on {
  background-color: rgba(62, 142, 115, 0.16) !important;
  border-left: 3px solid var(--ds-primary, #3e8e73);
}
.agenda-time-item.agenda-time-item--on .v-list-item__title {
  font-weight: 700;
}
.agenda-time-item__check {
  color: var(--ds-primary, #3e8e73) !important;
}

/* ── Horário ocupado — salvia-772 ─────────────────────────────────────────
   Três estados distintos no seletor, e a diferença importa:
     verde   = o médico atende e o horário está livre;
     laranja = COMPROMISSO do próprio médico (reunião, imprevisto);
     cinza   = já tem paciente marcado.
   Os dois últimos vêm com `item-disabled`, então o Vuetify já reduz a
   opacidade; aqui só damos a identidade de cor e a faixa lateral. */
.agenda-time-item.agenda-time-item--commitment {
  background-color: rgba(234, 88, 12, 0.14) !important;
  border-left: 3px solid var(--ds-cat-orange, #ea580c);
}
.agenda-time-item.agenda-time-item--commitment .v-list-item__title {
  text-decoration: line-through;
}
.agenda-time-item.agenda-time-item--taken {
  background-color: rgba(120, 144, 156, 0.14) !important;
  border-left: 3px solid var(--ds-text-soft, #78909c);
}
.agenda-time-item.agenda-time-item--taken .v-list-item__title {
  text-decoration: line-through;
}
.agenda-time-item__mark {
  color: var(--ds-cat-orange, #ea580c) !important;
}
.agenda-time-item.agenda-time-item--taken .agenda-time-item__mark {
  color: var(--ds-text-soft, #78909c) !important;
}

/* Dia de atendimento: FUNDO verde preenchendo o dia (em vez do pontinho).
   No Vuetify 2.7 o marcador é .v-date-picker-table__events > div. */
.agenda-highlight-picker .v-date-picker-table__events {
  top: 0 !important;
  bottom: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  line-height: 0;
  pointer-events: none;
  z-index: 0;
}
.agenda-highlight-picker .v-date-picker-table__events > div {
  display: block !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  border: none !important;
  border-radius: 50% !important;
  background-color: rgba(62, 142, 115, 0.24) !important;
}
.agenda-highlight-picker .v-date-picker-table .v-btn__content {
  position: relative;
  z-index: 1;
}
/* O dia selecionado fica sólido (esconde o destaque atrás dele) */
.agenda-highlight-picker
  .v-date-picker-table
  .v-btn--active
  .v-date-picker-table__events {
  display: none;
}
</style>
